import { match } from "ts-pattern";

export function separateLocale(locale: string) {
	const [language, country] = locale.toLowerCase().replace("-", "_").split("_");

	return { language: language ?? "nl", country: country ?? "nl" };
}

export function formatLocale(
	locale: string,
	format:
		| "language-country"
		| "language-COUNTRY"
		| "language_country"
		| "language_COUNTRY" = "language-country",
) {
	const { language, country } = separateLocale(locale);

	return match(format)
		.with("language-country", () => `${language}-${country}`)
		.with("language-COUNTRY", () => `${language}-${country.toUpperCase()}`)
		.with("language_country", () => `${language}_${country}`)
		.with("language_COUNTRY", () => `${language}_${country.toUpperCase()}`)
		.otherwise(() => locale);
}

type LocaleType =
	| "nl-nl"
	| "en-nl"
	| "de-at"
	| "en-at"
	| "fr-fr"
	| "en-fr"
	| "de-de"
	| "en-de"
	| "it-it"
	| "en-it"
	| "es-es"
	| "en-es"
	| "en-ie"
	| "nl-be"
	| "fr-be"
	| "en-be"
	| "de-lu"
	| "fr-lu"
	| "en-lu"
	| "en-pt"
	| "en-fi"
	| "en-cz"
	| "en-dk"
	| "en-se"
	| "en-pl";

type LanguageType = LocaleType extends `${infer Lang}-${infer _}` ? Lang : never;

type CountryType = LocaleType extends `${infer _}-${infer Country}` ? Country : never;

type PatternSupportedType =
	| { pattern: "language-country"; supported: LocaleType[] }
	| { pattern: "language"; supported: LanguageType[] }
	| { pattern: "country"; supported: CountryType[] };

type isLocaleType = { current: string } & PatternSupportedType;

export function isLocale({ current, pattern, supported }: isLocaleType) {
	const { language, country } = separateLocale(current);

	return match(pattern)
		.with("language", () => supported.includes(language))
		.with("country", () => supported.includes(country))
		.with("language-country", () => supported.includes(current))
		.exhaustive();
}

export function getDomainDisplayNames(locale: string) {
	const { language: localeLanguage, country: localeCountry } = separateLocale(locale);

	const region = new Intl.DisplayNames([localeLanguage], { type: "region" });
	const language = new Intl.DisplayNames([localeLanguage], { type: "language" });

	return { region: region.of(localeCountry.toUpperCase()), language: language.of(localeLanguage) };
}
